import React, { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import SystemCard from './SystemCard';
import { API_URL } from '../config';

function Overview({ products, category, loading, onProductClick, onSystemClick, isAuthenticated, currency, formatPrice, currencyLoading }) {
  // Меняем состояние с одиночного значения на массив выбранных фильтров
  const [selectedFilters, setSelectedFilters] = useState(['all']);
  const [filters, setFilters] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [systems, setSystems] = useState([]);
  const [filteredSystems, setFilteredSystems] = useState([]);
  // Добавляем состояния для второго фильтра
  const [sapsFilters, setSapsFilters] = useState(['all']); // Возможные значения для фильтра SAPS
  const [selectedSapsFilter, setSelectedSapsFilter] = useState('all');

  // Проверяем, есть ли хотя бы один продукт с sct_choice
  const hasAnySctChoice = products.some(product => product.sct_choice === true);

  // Fetch systems for marine category
  useEffect(() => {
    if (category === 'marine') {
      fetch(`${API_URL}/api/systems/by_category/?category=marine`)
        .then(res => res.json())
        .then(data => {
          setSystems(data);
        })
        .catch(err => console.error('Error fetching systems:', err));
    } else {
      setSystems([]);
    }
  }, [category]);

   // Получаем уникальные specification из treat_rates всех продуктов
  useEffect(() => {
    // Собираем все specification из treat_rates всех продуктов
    const allSpecifications = [];

    products.forEach(product => {
      if (product.treat_rates && Array.isArray(product.treat_rates)) {
        // Добавляем все specification из treat_rates
        product.treat_rates.forEach(treatRate => {
          if (treatRate.specification && !allSpecifications.includes(treatRate.specification)) {
            allSpecifications.push(treatRate.specification);
          }
        });
      }
    });

    // Сортируем по алфавиту для удобства
    const sortedSpecifications = allSpecifications.sort((a, b) => a.localeCompare(b));

    // Добавляем фильтр "all" в начало
    setFilters(['all', ...sortedSpecifications]);
    setSelectedFilters(['all']);
  }, [products]);

  // Получаем уникальные SAPS уровни из продуктов
  useEffect(() => {
    // Собираем все уникальные SAPS уровни
    const allSapsLevels = [];

    products.forEach(product => {
      if (product.saps_level && !allSapsLevels.includes(product.saps_level)) {
        allSapsLevels.push(product.saps_level);
      }
    });

    // Сортируем в логическом порядке: LOW, MID, FULL
    const sortedSapsLevels = allSapsLevels.sort((a, b) => {
      const order = { 'LOW': 1, 'MID': 2, 'FULL': 3 };
      return (order[a] || 999) - (order[b] || 999);
    });

    // Добавляем фильтр "all" в начало
    setSapsFilters(['all', ...sortedSapsLevels]);
    setSelectedSapsFilter('all');
  }, [products]);

  // Функция для обработки клика по фильтру
  const handleFilterClick = (filter) => {
    if (filter === 'all') {
      // Если кликнули на "all", то выбираем только его
      setSelectedFilters(['all']);
    } else {
      // Для обычных фильтров
      setSelectedFilters(prev => {
        // Убираем 'all' из выбранных, если он там есть
        const withoutAll = prev.filter(f => f !== 'all');

        if (withoutAll.includes(filter)) {
          // Если фильтр уже выбран, убираем его
          const newFilters = withoutAll.filter(f => f !== filter);
          // Если после удаления ничего не осталось, выбираем 'all'
          return newFilters.length > 0 ? newFilters : ['all'];
        } else {
          // Если фильтр не выбран, добавляем его
          return [...withoutAll, filter];
        }
      });
    }
  };

  // Фильтруем продукты на основе выбранных фильтров
  useEffect(() => {
    let filtered = products;

    // Применяем первый фильтр (specification) - множественный выбор
    if (!selectedFilters.includes('all') && selectedFilters.length > 0) {
      filtered = filtered.filter(product => {
        if (!product.treat_rates || !Array.isArray(product.treat_rates)) {
          return false;
        }
        // Проверяем, есть ли у продукта хотя бы одна specification из выбранных
        return product.treat_rates.some(treatRate =>
          selectedFilters.includes(treatRate.specification)
        );
      });
    }

    // Применяем второй фильтр (SAPS level)
    if (selectedSapsFilter !== 'all') {
      filtered = filtered.filter(product =>
        product.saps_level === selectedSapsFilter
      );
    }

    setFilteredProducts(filtered);

    // Для систем фильтруем по первому фильтру
    if (selectedFilters.includes('all') || selectedFilters.length === 0) {
      setFilteredSystems(systems);
    } else {
      setFilteredSystems(systems.filter(s =>
        selectedFilters.includes(s.system_type)
      ));
    }
  }, [selectedFilters, selectedSapsFilter, products, systems]);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  const getCategoryColor = () => {
    switch(category) {
      case 'hdmo': return '#059669';
      case 'pcmo': return '#DC2626';
      case 'cng': return '#7C3AED';
      case 'loco': return '#EA580C';
      case 'offroad': return '#0891B2';
      case 'marine': return '#0284C7';
      case 'gear': return '#CA8A04';
      case 'atf': return '#DB2777';
      case 'industrial': return '#64748B';
      default: return '#059669';
    }
  };

  return (
    <div id="ov">
      {/* Первый фильтр - Specifications с множественным выбором */}
      <div className="tf" id={`tf_${category}`}>
        {filters.map(filter => (
          <button
            key={filter}
            className={selectedFilters.includes(filter) ? 'on' : ''}
            onClick={() => handleFilterClick(filter)}
            style={selectedFilters.includes(filter) ? {
              color: getCategoryColor()
            } : {}}
          >
            {filter === 'all' ? 'All Specifications' : filter}
            {selectedFilters.includes(filter) && filter !== 'all' && (
              <span style={{ marginLeft: '5px' }}>✓</span>
            )}
          </button>
        ))}
      </div>



    {/* Второй фильтр - SAPS Levels */}
    <div className="tf" id={`tf_saps_${category}`} style={{ marginTop: '10px' }}>
      {sapsFilters.map(filter => (
        <button
          key={filter}
          className={selectedSapsFilter === filter ? 'on' : ''}
          onClick={() => setSelectedSapsFilter(filter)}
          style={selectedSapsFilter === filter ? {
            color: getCategoryColor()
          } : {}}
        >
          {filter === 'all' ? 'All SAPS' : filter}
        </button>
      ))}
    </div>

      <div className="G" id="cg">
        {/* Render systems first (for marine category) */}
        {filteredSystems.map(system => (
          <SystemCard
            key={system.id}
            system={system}
            onClick={onSystemClick}
            isAuthenticated={isAuthenticated}
          />
        ))}

        {/* Then render products */}
        {filteredProducts.map(product => (
          <ProductCard
            key={product.id}
            product={product}
            category={category}
            onClick={() => onProductClick(product)}
            isAuthenticated={isAuthenticated}
            hasAnySctChoice={hasAnySctChoice}
            currency={currency}
            formatPrice={formatPrice}
            currencyLoading={currencyLoading}
          />
        ))}
      </div>
    </div>
  );
}

export default Overview;
